import React, { useState, useEffect } from 'react';
import {
  Calendar, Download, FileText, RefreshCw, Loader2,
  TrendingUp, TrendingDown, Minus, Users, Car, Euro,
  AlertTriangle, Clock, CheckCircle, BarChart3
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import { apiRequest, API_BASE_URL, getAuthHeaders } from '../../../api/core.js';

/**
 * Format number with sign prefix, but no sign for zero
 */
const formatWithSign = (value, sign) => {
  const num = value || 0;
  if (num === 0) return '0';
  return `${sign}${num}`;
};

/**
 * Annual Summary Tab - "Ma synthèse de l'année"
 *
 * Yearly executive ledger with:
 * - Year picker
 * - Key metrics blocks
 * - Year-over-year comparison
 * - Auto-generated narrative
 * - CSV/PDF export
 */
function AnnualSummaryTab() {
  const { t, fmt } = useFormat();
  const money = (v) => fmt.money(v || 0, 'EUR', { decimals: 0 });
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showComparison, setShowComparison] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available years on mount
  useEffect(() => {
    fetchYears();
  }, []);

  // Fetch summary when year changes
  useEffect(() => {
    if (selectedYear) {
      fetchSummary(selectedYear);
    }
  }, [selectedYear]);

  const fetchYears = async () => {
    try {
      const result = await apiRequest('/reports/annual-summary/years');
      if (result.ok) {
        setYears(result.data);
        if (result.data.length > 0 && !result.data.find(y => y.year === selectedYear)) {
          setSelectedYear(result.data[0].year);
        }
      }
    } catch (err) {
      console.error('Failed to fetch years:', err);
      // Default to current year
      const currentYear = new Date().getFullYear();
      setYears([{ year: currentYear, hasSnapshot: false }]);
    }
  };

  const fetchSummary = async (year) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest(`/reports/annual-summary/${year}`);
      if (result.ok) {
        setSummaryData(result.data);
      } else {
        setError(result.error?.message || t('common.states.loadError'));
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
      setError(t('reports.annual.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await apiRequest(`/reports/annual-summary/${selectedYear}/generate`, { method: 'POST' });
      if (result.ok) {
        setSummaryData(result.data);
        fetchYears(); // Refresh years list
      } else {
        setError(result.error?.message || t('reports.annual.generateError'));
      }
    } catch (err) {
      console.error('Failed to generate summary:', err);
      setError(t('reports.annual.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reports/annual-summary/${selectedYear}/export/${format}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `synthese_annuelle_${selectedYear}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(t('reports.annual.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // Delta indicator component
  const DeltaIndicator = ({ current, previous, invertColors = false }) => {
    if (previous === undefined || previous === null || previous === 0) return null;
    const delta = ((current - previous) / previous * 100);
    const isPositive = delta > 0;
    const isNegative = delta < 0;

    let colorClass;
    if (invertColors) {
      colorClass = isPositive ? 'text-red-600' : isNegative ? 'text-emerald-600' : 'text-slate-500';
    } else {
      colorClass = isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-slate-500';
    }

    const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

    return (
      <span className={`inline-flex items-center gap-1 text-xs ${colorClass}`}>
        <Icon className="w-3 h-3" />
        {isPositive ? '+' : ''}{delta.toFixed(1)}%
      </span>
    );
  };

  // Metric card component
  const MetricCard = ({ label, value, delta, previousValue, icon: Icon, invertDelta }) => (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-semibold text-slate-800">{value}</span>
        {showComparison && (
          <DeltaIndicator
            current={typeof value === 'number' ? value : parseFloat(String(value).replace(/[^\d.-]/g, ''))}
            previous={previousValue}
            invertColors={invertDelta}
          />
        )}
      </div>
      {showComparison && previousValue !== undefined && (
        <div className="text-xs text-slate-400 mt-1">
          {t('reports.annual.previousYear')}: {typeof previousValue === 'number' ? fmt.number(previousValue) : previousValue}
        </div>
      )}
    </div>
  );

  // Section header component
  const SectionHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-2 mb-4 mt-6">
      {Icon && <Icon className="w-5 h-5 text-slate-600" />}
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">{t('common.states.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Year picker */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {years.map((y) => (
                <option key={y.year} value={y.year}>
                  {y.year} {y.hasSnapshot ? t('reports.annual.archivedSuffix') : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Compare toggle */}
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showComparison}
              onChange={(e) => setShowComparison(e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            {t('reports.annual.compareWithPrevious')}
          </label>
        </div>

        <div className="flex items-center gap-2">
          {/* Generate/refresh button */}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {summaryData?.cached ? t('common.actions.refresh') : t('reports.annual.generate')}
          </button>

          {/* Export buttons */}
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Summary content */}
      {summaryData && (
        <>
          {/* Status badge */}
          {summaryData.cached && summaryData.snapshotId && (
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              {t('reports.annual.snapshotArchivedOn', { date: fmt.date(summaryData.generatedAt) })}
              <span className="text-blue-400">| ID: {summaryData.snapshotId}</span>
            </div>
          )}

          {/* Perimeter & Context */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">
                {t('reports.annual.title', { year: selectedYear })}
              </h2>
              <div className="text-sm text-slate-500">
                {summaryData.perimeter?.periodStart} - {summaryData.perimeter?.periodEnd}
              </div>
            </div>

            {/* Key metrics grid */}
            <div className="grid grid-cols-4 gap-4">
              {/* Organization card - shows org name */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">{t('reports.annual.organization')}</span>
                </div>
                <div className="text-lg font-semibold text-slate-800">
                  {summaryData.perimeter?.organizationName || '-'}
                </div>
              </div>

              {/* Sites card - shows site names on 2 rows */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">{t('reports.annual.sites')} ({summaryData.perimeter?.sitesCount || 0})</span>
                </div>
                <div className="space-y-1">
                  {summaryData.perimeter?.sitesList?.slice(0, 3).map((site, idx) => (
                    <div key={idx} className="text-sm text-slate-700 truncate" title={site.name}>
                      {site.name}
                    </div>
                  )) || <div className="text-slate-400">-</div>}
                  {summaryData.perimeter?.sitesList?.length > 3 && (
                    <div className="text-xs text-slate-400">
                      {t('reports.annual.othersMore', { count: summaryData.perimeter.sitesList.length - 3 })}
                    </div>
                  )}
                </div>
              </div>

              <MetricCard
                label={t('reports.annual.vehiclesAvg')}
                value={summaryData.perimeter?.vehiclesAvgCount || 0}
                icon={Car}
              />
              <MetricCard
                label={t('reports.annual.driversAvg')}
                value={summaryData.perimeter?.driversAvgCount || 0}
                icon={Users}
              />
            </div>
          </div>

          {/* Fleet Evolution */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <SectionHeader title={t('reports.annual.fleetEvolution')} icon={Car} />
            <div className="grid grid-cols-5 gap-4">
              <MetricCard
                label={t('reports.annual.yearStart')}
                value={summaryData.fleetEvolution?.vehiclesStart || 0}
              />
              <MetricCard
                label={t('reports.annual.entries')}
                value={formatWithSign(summaryData.fleetEvolution?.vehiclesAdded, '+')}
              />
              <MetricCard
                label={t('reports.annual.exits')}
                value={formatWithSign(summaryData.fleetEvolution?.vehiclesRemoved, '-')}
              />
              <MetricCard
                label={t('reports.annual.yearEnd')}
                value={summaryData.fleetEvolution?.vehiclesEnd || 0}
              />
              <MetricCard
                label={t('reports.annual.rotationRate')}
                value={`${summaryData.fleetEvolution?.rotationRate || 0}%`}
              />
            </div>
            {summaryData.fleetEvolution?.fleetAgeAvg && (
              <div className="mt-3 text-sm text-slate-500">
                {t('reports.annual.fleetAvgAge', { years: summaryData.fleetEvolution.fleetAgeAvg })}
              </div>
            )}
          </div>

          {/* Drivers */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <SectionHeader title={t('drivers.title')} icon={Users} />
            <div className="grid grid-cols-5 gap-4">
              <MetricCard
                label={t('reports.annual.yearStart')}
                value={summaryData.drivers?.driversStart || 0}
              />
              <MetricCard
                label={t('reports.annual.entries')}
                value={formatWithSign(summaryData.drivers?.driversAdded, '+')}
              />
              <MetricCard
                label={t('reports.annual.exits')}
                value={formatWithSign(summaryData.drivers?.driversRemoved, '-')}
              />
              <MetricCard
                label={t('reports.annual.yearEnd')}
                value={summaryData.drivers?.driversEnd || 0}
              />
              <MetricCard
                label={t('reports.annual.licenseIncidents')}
                value={summaryData.drivers?.licenseNonComplianceEvents || 0}
                icon={AlertTriangle}
              />
            </div>
            {summaryData.drivers?.assignedPercent !== undefined && (
              <div className="mt-3 text-sm text-slate-500">
                {t('reports.annual.driversAssigned', { assigned: summaryData.drivers.assignedPercent, unassigned: summaryData.drivers.unassignedPercent })}
              </div>
            )}
          </div>

          {/* Costs */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <SectionHeader title={t('reports.annual.costs')} icon={Euro} />
            <div className="grid grid-cols-5 gap-4">
              <MetricCard
                label={t('reports.annual.totalCost')}
                value={money(summaryData.costs?.totalCost)}
                previousValue={summaryData.costsPreviousYear?.totalCost}
                invertDelta
              />
              <MetricCard
                label={t('common.costFamilies.fuel')}
                value={money(summaryData.costs?.fuelCost)}
                previousValue={summaryData.costsPreviousYear?.fuelCost}
                invertDelta
              />
              <MetricCard
                label={t('common.costFamilies.maintenance')}
                value={money(summaryData.costs?.maintenanceCost)}
                previousValue={summaryData.costsPreviousYear?.maintenanceCost}
                invertDelta
              />
              <MetricCard
                label={t('common.costFamilies.insurance')}
                value={money(summaryData.costs?.insuranceCost)}
                previousValue={summaryData.costsPreviousYear?.insuranceCost}
                invertDelta
              />
              <MetricCard
                label={t('reports.annual.perVehicle')}
                value={money(summaryData.costs?.costPerVehicle)}
              />
            </div>
          </div>

          {/* Risks */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <SectionHeader title={t('reports.annual.risksClaims')} icon={AlertTriangle} />
            <div className="grid grid-cols-4 gap-4">
              <MetricCard
                label={t('reports.annual.claimsDeclared')}
                value={summaryData.risks?.claimsTotal || 0}
              />
              <MetricCard
                label={t('reports.annual.claimsClosed')}
                value={summaryData.risks?.claimsClosed || 0}
              />
              <MetricCard
                label={t('reports.annual.claimsOpen')}
                value={summaryData.risks?.claimsOpen || 0}
              />
              {summaryData.risks?.avgClaimCycleDays && (
                <MetricCard
                  label={t('reports.annual.avgCycle')}
                  value={t('reports.annual.daysShort', { count: summaryData.risks.avgClaimCycleDays })}
                  icon={Clock}
                />
              )}
            </div>
          </div>

          {/* Availability */}
          {summaryData.availability && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <SectionHeader title={t('reports.annual.availabilityMaintenance')} icon={Clock} />
              <div className="grid grid-cols-4 gap-4">
                <MetricCard
                  label={t('reports.annual.availabilityRate')}
                  value={summaryData.availability.availabilityPercent !== null
                    ? `${summaryData.availability.availabilityPercent}%`
                    : 'N/A'}
                />
                <MetricCard
                  label={t('reports.annual.preventive')}
                  value={summaryData.availability.preventivePercent !== null
                    ? `${summaryData.availability.preventivePercent}%`
                    : 'N/A'}
                />
                <MetricCard
                  label={t('reports.annual.corrective')}
                  value={summaryData.availability.correctivePercent !== null
                    ? `${summaryData.availability.correctivePercent}%`
                    : 'N/A'}
                />
                <MetricCard
                  label={t('reports.annual.totalInterventions')}
                  value={summaryData.availability.totalMaintenanceCount || 0}
                />
              </div>
            </div>
          )}

          {/* Narrative */}
          {summaryData.narrative && summaryData.narrative.length > 0 && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                {t('reports.annual.keyPoints')}
              </h3>
              <ul className="space-y-2">
                {summaryData.narrative.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!summaryData && !loading && !error && (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            {t('reports.annual.noData', { year: selectedYear })}
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {t('reports.annual.noDataHint')}
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4" />
            )}
            {t('reports.annual.generateYear', { year: selectedYear })}
          </button>
        </div>
      )}
    </div>
  );
}

export default AnnualSummaryTab;
